import { jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from "react";
import { useCentralizedProviderData } from "@crehana/auth";
import { CREHANA_BRANDING } from "@crehana/constants/branding";
const OrganizationBrandingContext = /*#__PURE__*/ createContext(void 0);
const OrganizationBrandingProvider = ({ children })=>{
    const { currentPageOrganization, loading } = useCentralizedProviderData();
    const [branding, setBranding] = useState(CREHANA_BRANDING);
    useEffect(()=>{
        if (loading || !currentPageOrganization) return;
        applyBrandingStylesV1(currentPageOrganization.branding.paletteColors.v1);
        applyFavicon(currentPageOrganization.branding.imageFavicon.absoluteUrl);
        setBranding(currentPageOrganization.branding);
    }, [
        currentPageOrganization,
        loading
    ]);
    const updateBrandingData = (branding)=>{
        setBranding((prev)=>({
                ...prev,
                ...branding
            }));
        if (branding.paletteColors?.v1) applyBrandingStylesV1(branding.paletteColors.v1);
        if (branding.imageFavicon?.absoluteUrl) applyFavicon(branding.imageFavicon.absoluteUrl);
    };
    const context = {
        updateBrandingData,
        branding
    };
    return /*#__PURE__*/ jsx(OrganizationBrandingContext.Provider, {
        value: context,
        children: children
    });
};
const useOrganizationBranding = ()=>{
    const context = useContext(OrganizationBrandingContext);
    if (!context) throw new Error('Missing OrganizationBrandingContext');
    return context;
};
function applyFavicon(faviconUrl) {
    try {
        const existingFavicons = document.querySelectorAll("link[rel*='icon'], link[rel*='apple-touch-icon']");
        existingFavicons.forEach((link)=>link.remove());
        const favicon32 = document.createElement('link');
        favicon32.id = 'favicon';
        favicon32.rel = 'icon';
        favicon32.type = 'image/png';
        favicon32.setAttribute('sizes', '32x32');
        favicon32.href = faviconUrl;
        document.head.appendChild(favicon32);
        const shortcutIcon = document.createElement('link');
        shortcutIcon.id = 'lnkfavicon';
        shortcutIcon.rel = 'shortcut icon';
        shortcutIcon.type = 'image/png';
        shortcutIcon.href = faviconUrl;
        document.head.appendChild(shortcutIcon);
        const favicon64 = document.createElement('link');
        favicon64.rel = 'icon';
        favicon64.type = 'image/png';
        favicon64.setAttribute('sizes', '64x64');
        favicon64.href = faviconUrl;
        document.head.appendChild(favicon64);
        const appleTouchIcon = document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        appleTouchIcon.href = faviconUrl;
        document.head.appendChild(appleTouchIcon);
    } catch (error) {
        console.error('Error applying favicon:', error);
    }
}
function applyBrandingStylesV1(colors) {
    try {
        const root = document.documentElement;
        root.style.setProperty('--cds-brand-90', colors.darkest);
        root.style.setProperty('--cds-brand-50', colors.dark);
        root.style.setProperty('--cds-brand-40', colors.base);
        root.style.setProperty('--cds-brand-30', colors.light);
        root.style.setProperty('--cds-brand-10', colors.lightest);
        root.style.setProperty('--color-primary-light-700', colors.darkest);
        root.style.setProperty('--color-primary-light-600', colors.darkest);
        root.style.setProperty('--color-primary-light-500', colors.dark);
        root.style.setProperty('--color-primary-light-400', colors.base);
        root.style.setProperty('--color-primary-light-300', colors.light);
        root.style.setProperty('--color-primary-light-200', colors.lightest);
        root.style.setProperty('--color-primary-light-100', colors.lightest);
        root.style.setProperty('--color-primary-dark-700', colors.darkest);
        root.style.setProperty('--color-primary-dark-600', colors.base);
        root.style.setProperty('--color-primary-dark-500', colors.light);
        root.style.setProperty('--color-primary-dark-400', colors.light);
        root.style.setProperty('--color-primary-dark-300', colors.light);
        root.style.setProperty('--color-primary-dark-200', colors.lightest);
        root.style.setProperty('--color-primary-dark-100', colors.lightest);
        root.style.setProperty('--color-primary-dark', colors.darkest);
        root.style.setProperty('--color-primary-lighter', colors.light);
        root.style.setProperty('--color-primary-light', colors.base);
        root.style.setProperty('--color-primary-main', colors.dark);
    } catch (error) {
        console.error('Error applying branding styles:', error);
    }
}
export { OrganizationBrandingProvider, useOrganizationBranding };
